// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { useAppSelector, useAppDispatch } from '@advisor-app/store';
import { reportingApi } from '@shared/data-access/api/ai/reporting-api';
import {
  MODAL_TEMPLATES_OPTIONS,
  showModal,
} from '@shared/ui-kit/components/popups/show-modal';
import { selectLimitedComparisonState } from '@advisor-app/store/slices/my-research';
import type { ComparisonReport } from '@shared/data-access/interfaces/api/ai/reporting';
import styles from '@shared/ui-kit/components/popups/show-modal/modal.module.css';
import { downloadFile } from '@shared/data-access/utils/download-file';
import { useTranslation } from 'react-i18next';

const inputProps = {
  type: 'text',
  className: styles.input,
};

export const useDownloadResearchReport = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const sixComparisonAssets = useAppSelector(selectLimitedComparisonState);

  const { assetsComparison, assetsInfo } = sixComparisonAssets;

  const comparisonPayload: ComparisonReport = {
    ...assetsComparison,
    assets_info: assetsInfo,
  };

  const handleDownloadReport = async (
    hasMoreThanSixAssets: boolean,
  ): Promise<void> => {
    showModal({
      ...MODAL_TEMPLATES_OPTIONS(t).DOWNLOAD_RESEARCH_REPORT,
      title: t('common.download-report', { ns: 'common' }),
      buttons: {
        cancel: {
          show: true,
          text: t('common.cancel', { ns: 'common' }),
        },
        confirm: {
          show: true,
          text: t('common.download', { ns: 'common' }),
        },
      },
      content: (
        <form id="downloadReportForm">
          <div className="flex flex-col">
            <input
              name="reportName"
              placeholder={t('common.report-name', { ns: 'common' })}
              id="report-name"
              {...inputProps}
              required
            />
            {hasMoreThanSixAssets && (
              <span className="ui-modal-content italic">
                {t('research.report-include-six-assets', { ns: 'research' })}
              </span>
            )}
          </div>
        </form>
      ),
      preConfirm: /* istanbul ignore next */ async () => {
        const form = document.querySelector(
          '#downloadReportForm',
        ) as HTMLFormElement;

        const formData = new FormData(form);

        const report_name = formData.get('reportName') as string;

        if (!report_name) {
          showModal({
            ...MODAL_TEMPLATES_OPTIONS(t).UNHANDLED_ERROR,
            title: t('research.unnamed-report-warning', { ns: 'research' }),
            content: t('research.unnamed-report-message', { ns: 'research' }),
            buttons: {
              cancel: { show: false },
              confirm: { show: true, text: 'Back' },
            },
          });
          return false;
        }

        const data = await dispatch(
          reportingApi.endpoints.researchReport.initiate({
            report_type: 'comparison',
            payload: {
              report_name: report_name,
              data: comparisonPayload,
            },
          }),
        ).unwrap();

        downloadFile(data);
      },
    }).then(result => {
      /* istanbul ignore next */
      if (result.isConfirmed && typeof result.value !== 'boolean') {
        showModal({
          ...MODAL_TEMPLATES_OPTIONS(t).UNHANDLED_ERROR,
          title: t('research.report-error', { ns: 'research' }),
          content: t('research.report-error-message', { ns: 'research' }),
          buttons: { confirm: { show: true, text: 'Ok' } },
        });
      } else if (result.isConfirmed) {
        showModal({
          content: t('research.report-success', { ns: 'research' }),
          buttons: { confirm: { show: true, text: 'Ok' } },
        });
      }
    });
  };

  return { handleDownloadReport };
};
